// Single source of truth for the strangler routing decision. It now lives in professional-area's
// BFF (POST /api/migrated-routes), not web-api. We hit it with a RELATIVE URL: eproject runs in the
// browser on the same host that Next serves, and /api/* is not proxied to eproject — so a relative
// fetch reaches professional-area's route handler directly. `credentials: 'include'` forwards the
// vibia_jwt cookie so the handler resolves the caller (per-user toggle targeting) exactly like the
// professional-area Proxy does; no user object is sent explicitly.
const MIGRATED_ROUTES_URL = '/api/migrated-routes'

// Short in-memory cache: this is consulted on every route change (see StranglerGuard), so we
// avoid hitting the endpoint on every single navigation while still picking up a login/logout
// or a toggle flip within a reasonable window.
const CACHE_TTL_MS = 30000

let cache = null // { value, expiresAt }
let pending = null // in-flight promise, avoids duplicate concurrent requests on rapid navigation

export async function getMigratedRoutes() {
	const now = Date.now()
	if (cache && cache.expiresAt > now) {
		return cache.value
	}
	if (pending) {
		return pending
	}

	pending = fetch(MIGRATED_ROUTES_URL, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: '{}',
	})
		.then((response) => {
			if (!response.ok) {
				throw new Error(`migrated-routes responded ${response.status}`)
			}
			return response.json()
		})
		.then((routes) => {
			cache = { value: routes, expiresAt: Date.now() + CACHE_TTL_MS }
			return routes
		})
		.catch((error) => {
			console.warn('[strangler] Failed to fetch migrated routes, staying on legacy', error)
			return []
		})
		.finally(() => {
			pending = null
		})

	return pending
}

export function __clearMigratedRoutesCache() {
	cache = null
}

/**
 * Synchronous, best-effort read of the last fetched list — even if its TTL has technically
 * expired. Lets StranglerGuard render optimistically on the very next tick instead of blanking
 * out (unmounting) the whole legacy router on every single navigation while a fresh fetch is
 * in flight. null only on the very first check of the whole session, before anything has ever
 * been fetched.
 */
export function peekCachedMigratedRoutes() {
	return cache ? cache.value : null
}
